import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import {
  sessionsFixture,
  revokeSessionFixture,
  updateProfileFixture,
  changePasswordFixture,
  mfaStatusFixture,
  mfaSetupFixture,
  mfaVerifyFixture,
  mfaDisableFixture,
  mfaRegenerateBackupCodesFixture,
} from './fixture';

/* ── Profile ───────────────────────────────────────────────────────── */

export function useUpdateMyProfile() {
  return useApiMutation({
    live: (fetch, patch) => fetch('/v1/operator/me', { method: 'PUT', body: patch }),
    fixture: (patch) => updateProfileFixture(patch),
    invalidate: ['operatorMe'], // MeProvider liest das (5min drift refresh nochmal)
    successMessage: 'Profil aktualisiert.',
  });
}

export function useChangeMyPassword() {
  return useApiMutation({
    live: (fetch, body) => fetch('/v1/operator/me/change-password', { method: 'POST', body }),
    fixture: (body) => changePasswordFixture(body),
    invalidate: ['mfaStatus'],
    successMessage: 'Passwort geändert.',
  });
}

/* ── Sessions ──────────────────────────────────────────────────────── */

export function useMySessions() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mySessions', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return sessionsFixture();
      }
      // Core hat /me/sessions noch nicht überall live (ERRORs.md Error-04):
      // → 404 als „keine Session-Liste verfügbar" lesen statt Page-Crash.
      try {
        return await fetch('/v1/operator/me/sessions');
      } catch (err) {
        if (err?.status === 404) return { sessions: [], unavailable: true };
        throw err;
      }
    },
  });
}

export function useRevokeSession() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/operator/me/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    fixture: ({ id }) => revokeSessionFixture(id),
    invalidate: ['mySessions'],
    successMessage: 'Session beendet.',
  });
}

/* ── MFA ───────────────────────────────────────────────────────────── */

export function useMfaStatus() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mfaStatus', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return mfaStatusFixture();
      }
      // Core hat /me/mfa nicht in jedem Deploy live (ERRORs.md Error-04):
      // → 404 als „MFA-Endpoint nicht verfügbar" lesen, damit die ganze
      // /me/security-Page noch funktioniert.
      try {
        return await fetch('/v1/operator/me/mfa');
      } catch (err) {
        if (err?.status === 404) return { enabled: false, unavailable: true };
        throw err;
      }
    },
  });
}

/** Startet das Setup. Returnt provisioning-URL + secret (otpauth-Format). */
export function useMfaSetup() {
  return useApiMutation({
    live: (fetch) => fetch('/v1/operator/me/mfa/setup', { method: 'POST' }),
    fixture: () => mfaSetupFixture(),
    // Kein Toast — Setup-Flow zeigt QR direkt
  });
}

/** Verifiziert den TOTP-Code und aktiviert MFA. Returnt Backup-Codes. */
export function useMfaVerify() {
  return useApiMutation({
    live: (fetch, body) => fetch('/v1/operator/me/mfa/verify', { method: 'POST', body }),
    fixture: ({ code }) => mfaVerifyFixture(code),
    invalidate: ['mfaStatus', 'operatorMe'],
    // Kein Toast — Backup-Codes kommen im OneShotSecretReveal
  });
}

export function useMfaDisable() {
  return useApiMutation({
    live: (fetch, body) => fetch('/v1/operator/me/mfa', { method: 'DELETE', body }),
    fixture: (body) => mfaDisableFixture(body),
    invalidate: ['mfaStatus', 'operatorMe'],
    successMessage: 'MFA deaktiviert.',
  });
}

export function useRegenerateBackupCodes() {
  return useApiMutation({
    live: (fetch, body) =>
      fetch('/v1/operator/me/mfa/backup-codes', { method: 'POST', body }),
    fixture: () => mfaRegenerateBackupCodesFixture(),
    // Kein Toast — neue Codes kommen im OneShotSecretReveal
  });
}
